import type { DocumentMeta, Point, RGBA, SelectionCombineMode } from '../core/types';
import { getEngine } from '../engine/engine';
import { selectionOp, type EngineHistoryOp, type FullSnapshot } from '../engine/historyOps';
import {
  applySelectionCoverage,
  colorRangeSelection,
  growSelection,
  modifySelection,
  selectionImage,
  similarSelection,
  wandSelection,
  type SelectionModifyKind,
} from '../engine/selectionOps';
import type { ColorSelectImage } from '../selection/colorSelect';
import { activeDocument, commitHistory, syncSelectionFlag } from './actions';
import { useAppStore, type AppState } from './store';

const engine = getEngine();

function state(): AppState {
  return useAppStore.getState();
}

/** Every selection change ends the same way: flag, history, repaint. */
function commitSelection(doc: DocumentMeta, label: string, ops: EngineHistoryOp[] | null): void {
  if (!ops) return;
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, label, ops);
}

export function reselect(): void {
  const doc = activeDocument();
  if (!doc) return;
  const selection = engine.resourcesFor(doc).selection;
  const before = engine.snapshotSelection(doc);
  if (!selection.reselect()) {
    state().setStatus('Nothing to reselect');
    return;
  }
  commitSelection(doc, 'Reselect', [selectionOp(before, engine.snapshotSelection(doc))]);
}

const MODIFY_LABELS: Record<SelectionModifyKind, string> = {
  border: 'Border Selection',
  smooth: 'Smooth Selection',
  expand: 'Expand Selection',
  contract: 'Contract Selection',
  feather: 'Feather Selection',
};

export function modifySelectionBy(kind: SelectionModifyKind, amount: number): void {
  const doc = activeDocument();
  if (!doc) return;
  const ops = modifySelection(engine, doc, kind, amount);
  if (!ops) {
    state().setStatus('Select an area first');
    return;
  }
  commitSelection(doc, MODIFY_LABELS[kind], ops);
}

export function growSelectionByColor(): void {
  const doc = activeDocument();
  if (!doc) return;
  const { wandTolerance, wandSampleAllLayers } = state().toolOptions;
  commitSelection(doc, 'Grow', growSelection(engine, doc, wandTolerance, wandSampleAllLayers));
}

export function selectSimilarColors(): void {
  const doc = activeDocument();
  if (!doc) return;
  const { wandTolerance, wandSampleAllLayers } = state().toolOptions;
  commitSelection(doc, 'Similar', similarSelection(engine, doc, wandTolerance, wandSampleAllLayers));
}

export function magicWandAt(point: Point, mode: SelectionCombineMode): void {
  const doc = activeDocument();
  if (!doc) return;
  const options = state().toolOptions;
  const ops = wandSelection(
    engine,
    doc,
    point,
    mode,
    {
      tolerance: options.wandTolerance,
      contiguous: options.wandContiguous,
      antialias: options.selectionAntialias,
      sampleAllLayers: options.wandSampleAllLayers,
    },
    options.selectionFeather,
  );
  commitSelection(doc, 'Magic Wand', ops);
}

/**
 * A drag that keeps refining the selection, such as Quick Selection. The mask is
 * updated on every step so the marching ants follow the pointer, but only one
 * history entry is recorded, taken against the state before the drag started.
 */
let session: { docId: string; before: FullSnapshot | null } | null = null;

export function beginSelectionSession(): void {
  const doc = activeDocument();
  if (!doc) return;
  session = { docId: doc.id, before: engine.snapshotSelection(doc) };
}

export function updateSelectionCoverage(
  coverage: Uint8Array,
  mode: SelectionCombineMode,
  feather = 0,
): void {
  const doc = activeDocument();
  if (!doc || session?.docId !== doc.id) return;
  engine.resourcesFor(doc).selection.restore(session.before?.data ?? null);
  applySelectionCoverage(engine, doc, coverage, mode, feather);
  syncSelectionFlag(doc.id);
  engine.invalidate();
}

export function endSelectionSession(label: string): void {
  const doc = activeDocument();
  const current = session;
  session = null;
  if (!doc || !current || current.docId !== doc.id) return;
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, label, [selectionOp(current.before, engine.snapshotSelection(doc))]);
}

export function applyColorRange(
  samples: readonly RGBA[],
  fuzziness: number,
  invert: boolean,
  mode: SelectionCombineMode,
): void {
  const doc = activeDocument();
  if (!doc) return;
  commitSelection(
    doc,
    'Color Range',
    colorRangeSelection(engine, doc, samples, fuzziness, invert, mode),
  );
}

/** Image the Color Range dialog samples from and previews. */
export function colorRangeImage(): ColorSelectImage | null {
  const doc = activeDocument();
  if (!doc) return null;
  return selectionImage(engine, doc, true);
}

/** What the Refine Edge workspace works on: the flat image and the current mask. */
export function refineEdgeInputs(): { image: ColorSelectImage; coverage: Uint8Array } | null {
  const doc = activeDocument();
  if (!doc) return null;
  const selection = engine.resourcesFor(doc).selection;
  if (selection.isEmpty) return null;
  const image = selectionImage(engine, doc, true);
  return image ? { image, coverage: selection.snapshot() } : null;
}

export function applyRefinedSelection(coverage: Uint8Array): void {
  const doc = activeDocument();
  if (!doc) return;
  const before = engine.snapshotSelection(doc);
  engine.resourcesFor(doc).selection.restore(coverage);
  commitSelection(doc, 'Refine Edge', [selectionOp(before, engine.snapshotSelection(doc))]);
}
